import { forwardRef, ReactElement } from 'react'
import { Box, Chip, Skeleton } from '@mui/material'
import MuxPlayer from '@mux/mux-player-react'
import type MuxPlayerElement from '@mux/mux-player'
import { useVideoTranscriptVtt } from 'src/v3/classroom-v2/hooks/use-video-transcript-vtt'
import { CourseV2VideoStep } from 'src/v3/classroom-v2/types'

// The plain player, with no knowledge of compliance gating or telemetry.
// Enforcement (seek/rate locking, progress posting, completion validation,
// interruption dialogs) is a capability layered on top by whichever caller
// needs it — see ENG-432 (compliance mode) and ENG-433 (free-roam). This
// component's only job is to play the video, resume at the given start
// time, and show subtitles when available.
export type VideoPlayerProps = {
  courseID: string
  video: CourseV2VideoStep
  userID: string
  startTime?: number
  onTimeUpdate?: (event: Event) => void
  onRateChange?: (event: Event) => void
  onEnded?: (event: Event) => void
  // Free-roam's required visual confirmation that anti-cheat/telemetry
  // aren't active (ENG-433) — the base player itself has no notion of
  // mode, so the caller decides whether to show this.
  showReviewModeBadge?: boolean
}

// Shared by VideoPlayer's own loading state and by course-player.tsx's
// fallback for step types that have no player yet — both need the same
// video-shaped placeholder, not a generic loading strip. A plain
// variant="rounded" Skeleton with aspectRatio in its sx won't actually hold
// that ratio: Skeleton applies its own explicit default height, and CSS
// aspect-ratio has no effect once a dimension is already explicitly set
// (not auto). Wrapping it in a Box that owns the aspect ratio, with the
// Skeleton stretched to 100%/100% inside, sidesteps that.
export let VideoPlayerSkeleton = (): ReactElement => (
  <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
    <Skeleton variant="rounded" sx={{ width: '100%', height: '100%' }} />
  </Box>
)

export let VideoPlayer = forwardRef<MuxPlayerElement, VideoPlayerProps>(
  (
    {
      courseID,
      video,
      userID,
      startTime,
      onTimeUpdate,
      onRateChange,
      onEnded,
      showReviewModeBadge,
    },
    ref,
  ): ReactElement => {
    let { vttUrl, isSettled } = useVideoTranscriptVtt(courseID, video.id)

    // <mux-player> doesn't reliably pick up a <track> child added after its
    // initial mount, so the player itself must not mount until the
    // transcript check has settled — found or confirmed absent — rather
    // than mounting immediately and adding the <track> once the fetch
    // resolves. A missing transcript is a valid, expected outcome (not
    // every video has one), so this skeleton is brief either way.
    if (!isSettled) {
      return <VideoPlayerSkeleton />
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
        {showReviewModeBadge && (
          <Chip
            label="Review Mode"
            size="small"
            color="warning"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 10,
              fontWeight: 600,
            }}
          />
        )}
        <MuxPlayer
          ref={ref}
          title={video.name}
          src={video.videoUrl.replace('mp4', 'm3u8')}
          accentColor="#f9ea4f"
          primaryColor="#6694E9"
          backwardSeekOffset={20}
          forwardSeekOffset={20}
          metadata={{
            video_id: video.id,
            video_title: video.name,
            viewer_user_id: userID,
          }}
          onTimeUpdate={onTimeUpdate}
          onRateChange={onRateChange}
          onEnded={onEnded}
          defaultHiddenCaptions={!vttUrl}
          streamType="on-demand"
          startTime={startTime ?? 0}
        >
          {vttUrl && (
            <track
              src={vttUrl}
              kind="subtitles"
              label="English"
              srcLang="en"
              default
            />
          )}
        </MuxPlayer>
      </Box>
    )
  },
)
